import { inject, Injectable } from '@angular/core';
import { TemplateCriteriaStore } from '../store/template-criteria.store';
import { PlayService, SnackBarService } from '@r1-compass-library/services';
import { MatDialog } from '@angular/material/dialog';
import { TemplateCriteriaValidationStateService } from '../template-criteria-validation-state.service';
import { Play, TemplateCriteria } from '@r1-compass-library/models';
import { of, switchMap, firstValueFrom, Observable, tap } from 'rxjs';
import { Dependency } from '@r1-compass-library/models/dependencies.model';
import {
    DisplayAssetDependencies,
    DisplayAssetDependenciesListComponent,
} from '@r1-compass-library/components/display-asset-dependencies-list/display-asset-dependencies-list.component';
import { TemplateCriteriaEnableComponent } from './template-criteria-enable.component';
import { rxMutation } from '@angular-architects/ngrx-toolkit';
import { noopFormSaveMutationResult } from '@r1-compass-library/guards/hasUnsavedChanges.guard';
import { EnableService, ToggleOutcome } from '@r1-compass-library/features/validation-sync';
import { PlayRevalidationService } from '../../plays/play-revalidation.service';

@Injectable({
    providedIn: TemplateCriteriaEnableComponent,
})
export class TemplateCriteriaEnableService implements EnableService<TemplateCriteria, Play> {
    readonly #store = inject(TemplateCriteriaStore);
    readonly #playService = inject(PlayService);
    readonly #dialog = inject(MatDialog);
    readonly #validationStateService = inject(TemplateCriteriaValidationStateService);
    readonly #snackBarService = inject(SnackBarService);
    readonly #playRevalidationService = inject(PlayRevalidationService);
    readonly #templateCriteriaValidationStateService = inject(TemplateCriteriaValidationStateService);

    public enable = rxMutation<void, ToggleOutcome<Play>>({
        operation: (_: void) => {
            const isValid = this.#validationStateService.invalidSections().length === 0;
            if (!isValid) {
                return of({ shouldSetEnabled: false });
            }
            return of({ shouldSetEnabled: true });
        },
        onSuccess: res => {
            if (!res.shouldSetEnabled) {
                this.#snackBarService.openGenericSnackbar({
                    messages: [
                        'This template criteria is invalid and cannot be enabled. Please review and resolve validation errors and try again.',
                    ],
                    verticalPosition: 'top',
                    panelClass: 'generic-snack-bar-warning',
                    showDismissButton: true,
                });
            }
        },
    });

    public disable = rxMutation<TemplateCriteria, ToggleOutcome<Play>>({
        operation: (tc: TemplateCriteria) => {
            return this.#playService.search({ templateCriteriaId: tc.id, deleted: 'False', enabled: 'True' }).pipe(
                switchMap(plays => {
                    if (plays.length > 0) {
                        return of({
                            shouldSetEnabled: true,
                            dependencies: plays,
                        });
                    }
                    return of({ shouldSetEnabled: false });
                })
            );
        },
        onSuccess: (res, args) => {
            if ((res.dependencies ?? []).length > 0) {
                this.openDependencyDialog(args.id, res.dependencies ?? []);
            }
        },
    });

    private openDependencyDialog(templateCriteriaId: number, plays: Play[]) {
        const dependencies: Dependency[] = plays.map(play => ({
            playId: play.id,
            name: play.name,
            assetId: templateCriteriaId,
        }));
        this.#dialog.open<DisplayAssetDependenciesListComponent, DisplayAssetDependencies>(
            DisplayAssetDependenciesListComponent,
            {
                width: '600px',
                disableClose: true,
                data: {
                    dependencies: dependencies,
                    dialogText: `Can't disable template criteria because the following enabled plays depend on it:`,
                },
                autoFocus: false,
            }
        );
    }

    public async save(userSetEnabled: boolean | undefined) {
        const templateCriteriaId = this.#store.template()?.id;
        if (!templateCriteriaId || userSetEnabled === undefined) {
            return noopFormSaveMutationResult();
        }

        const wasOriginallyEnabled = this.#store.template()?.enabled ?? false;

        if (wasOriginallyEnabled && !userSetEnabled) {
            await firstValueFrom(this.invalidatePlayDependencies(templateCriteriaId));
        }

        const res = await this.#store.saveTemplate({
            templateId: templateCriteriaId,
            template: {
                enabled: userSetEnabled,
                valid: this.#templateCriteriaValidationStateService.invalidSections().length === 0,
            },
        });

        if (res.status === 'success' && !wasOriginallyEnabled && userSetEnabled) {
            await firstValueFrom(
                this.#playRevalidationService.revalidatePlaysForEntity('templateCriteria', templateCriteriaId)
            );
        }

        return res;
    }

    private invalidatePlayDependencies(id: number): Observable<Play[]> {
        return this.#playService.search({ templateCriteriaId: id, deleted: 'False' }).pipe(
            switchMap(plays => {
                const playsToInvalidate = plays.filter(p => p.valid);
                if (playsToInvalidate.length === 0) {
                    return of<Play[]>([]);
                }
                const playIdsToInvalidate = playsToInvalidate.map(p => p.id);
                return this.#playService.patchPlays({ ids: playIdsToInvalidate }, { valid: false }).pipe(
                    tap(() => {
                        const dependencies: Dependency[] = playsToInvalidate.map(play => ({
                            playId: play.id,
                            name: play.name,
                            assetId: id,
                        }));
                        this.#dialog.open<DisplayAssetDependenciesListComponent, DisplayAssetDependencies>(
                            DisplayAssetDependenciesListComponent,
                            {
                                width: '600px',
                                disableClose: true,
                                data: {
                                    dependencies,
                                    dialogText: `The following play${playsToInvalidate.length > 1 ? 's were' : ' was'} updated to invalid:`,
                                    dialogTitle: 'Plays Updated',
                                    cancelButtonText: 'Close',
                                },
                                autoFocus: false,
                            }
                        );
                    })
                );
            })
        );
    }
}
